import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'danger';
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((accepted: boolean) => {
    setOptions(null);
    resolveRef.current?.(accepted);
    resolveRef.current = null;
  }, []);

  const confirm = useCallback<Confirm>((opts) => {
    // Hộp thoại trước còn treo thì trả false cho nó, tránh promise không bao giờ settle.
    resolveRef.current?.(false);
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <div className="bg-canvas/80 fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="border-line bg-panel w-full max-w-md rounded-2xl border p-6 text-left shadow-xl">
            <h3 className="text-ink mb-2 text-lg font-bold">{options.title}</h3>
            <p className="text-ink-muted mb-6 text-sm">{options.message}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  settle(false);
                }}
                className="border-line-soft text-ink-dim hover:bg-line-soft hover:text-ink rounded-lg border bg-transparent px-4 py-2 text-sm font-semibold transition-all"
              >
                {options.cancelText ?? 'Huỷ'}
              </button>
              <button
                type="button"
                onClick={() => {
                  settle(true);
                }}
                className={`rounded-lg px-4 py-2 text-sm font-bold text-white shadow-sm transition-all ${
                  options.type === 'danger'
                    ? 'bg-danger hover:bg-danger-hover'
                    : 'bg-accent hover:bg-accent-hover'
                }`}
              >
                {options.confirmText ?? 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm phải nằm trong ConfirmProvider');
  return ctx;
}
